import { StripePostalAddress } from '@commutifi/models/shared/StripePostalAddress';
import { format, CommonOptions } from '@fragaria/address-formatter';
import { Address } from 'shared/interfaces';

/** Makes all properties of T accept null in addition to their original types */
type NullablePartial<T> = { [P in keyof T]?: T[P] | null };
const isStripePostalAddress = (address: NullablePartial<Address> | StripePostalAddress): address is StripePostalAddress => Object.prototype.hasOwnProperty.call(address, 'line1');
/**
 * See \@fragaria/address-formatter
 * Inline is the default format and you can pass
 * an option to display a multiline address (format: array).
 * To add the country use option country (appendCountry: true)
 * For more details please see their doc https://github.com/fragaria/address-formatter
 */
export function formatAddress<T extends 'array' | 'string' = 'string'>(address: NullablePartial<Address> | StripePostalAddress | undefined, options: CommonOptions & {
  output?: T;
  fallbackCountryCode?: string;
} = {}): T extends 'array' ? string[] : string {
  if (!address) {
    // If the address is undefined, return an empty string or empty array based on the expected return type
    return (options.output === 'array' ? [''] : '') as T extends 'array' ? string[] : string;
  }
  const normalizedAddress: NullablePartial<Address> = isStripePostalAddress(address) ? {
    street: address.line1,
    city: address.city || '',
    postalCode: address.postal_code || '',
    country: address.country,
    state: address.state || ''
  } : address;
  const {
    fallbackCountryCode,
    ...restOptions
  } = options;
  if (!normalizedAddress.street && !normalizedAddress.city && !normalizedAddress.postalCode) {
    return (options.output === 'array' ? [] : '') as T extends 'array' ? string[] : string;
  }
  return format({
    houseNumber: normalizedAddress.streetNumber ?? undefined,
    road: normalizedAddress.street ?? undefined,
    city: normalizedAddress.city ?? undefined,
    postcode: normalizedAddress.postalCode ?? undefined,
    state: normalizedAddress.state ?? undefined,
    countryCode: normalizedAddress.country ?? undefined
  },
  // They have 2 time the same function (overwrite of params) defined which confuses typescript
  // @ts-expect-error -- Function overwrites confuses typescript
  {
    output: 'string',
    fallbackCountryCode: fallbackCountryCode || 'US',
    ...restOptions
  });
}